import React, { useState } from "react";
import { Image, StyleSheet, View, ViewStyle } from "react-native";
import { AntDesign } from "@expo/vector-icons";
import { useTheme, TouchableRipple } from "react-native-paper";
import { useSelector } from "react-redux";
import { ThemeType } from "../../theme/theme";
import { Text } from "../text/text";
import { Stack } from "../layout/stack";
import { InfoBoxType, InfoBoxIconType } from "../../enums";

/**
 *
 * A box with a light gradient background
 */
interface InfoBoxProps extends Record<string, any> {
  width?: number;
  headline: string;
  style?: any;
  // This could be abstracted but for now only x resides here
  usesXClose?: boolean;
  xCloseHandler?: () => any;
  headlineIconType?: InfoBoxIconType;
  infoBoxType: InfoBoxType;
  borderRadius?: number;
  iconName?: string;
}

export const InfoBox = ({
  width,
  headline,
  children,
  style,
  infoBoxType,
  usesXClose,
  xCloseHandler,
  iconName,
  headlineIconType = InfoBoxIconType.EXCLAMATION,
  borderRadius = 5,
}: InfoBoxProps) => {
  const theme = useTheme() as ThemeType;
  const [isClosed, setIsClosed] = useState(false);
  if (isClosed) {
    return null;
  }
  const { borderColor, backgroundColor } = getBorderAndFillColor(infoBoxType, theme);
  const styles = StyleSheet.create({
    container: {
      display: "flex",
      width,
      flexDirection: "column",
      padding: theme.spacing.small,
      borderWidth: 1,
      borderColor,
      borderRadius: borderRadius,
      backgroundColor,
      ...style,
    },
    headlineContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-start",
      alignItems: "center",
      width: "100%",
    },
    iconWidth: {
      width: theme.iconSize.smaller,
      height: theme.iconSize.smaller,
    },
    headlineText: {
      marginLeft: theme.spacing.small,
      flexWrap: "wrap",
      width: "80%",
      fontWeight: "bold",
      fontSize: theme.fontSize.medium,
    },
  });
  const defaultXCloseHandler = () => {
    setIsClosed(true);
  };
  const headlineIcon = getHeadlineIcon(headlineIconType, iconName, theme);
  return (
    <Stack space="smaller" useThemeBackgroundColor={false} style={styles.container}>
      <View style={styles.headlineContainer}>
        {headlineIcon}
        <Text style={styles.headlineText}>{headline}</Text>
        {usesXClose ? (
          <TouchableRipple
            onPress={xCloseHandler || defaultXCloseHandler}
            style={{ width: "15%", alignItems: "end", marginLeft: 10 }}>
            <Image
              onPress={setIsClosed}
              style={styles.iconWidth}
              source={require("../../../assets/x.png")}></Image>
          </TouchableRipple>
        ) : null}
      </View>
      {children}
    </Stack>
  );
};

export const OnlyHeadlineInfoBox = ({
  width,
  headline,
  children,
  infoBoxType,
  usesXClose,
  headlineWidth = 280,
  headlineIconType = InfoBoxIconType.EXCLAMATION,
  borderRadius = 0,
}: InfoBoxProps) => {
  const theme = useTheme() as ThemeType;
  const logger = useSelector((state: RootState) => state.logger);
  const [isClosed, setIsClosed] = useState(false);
  if (isClosed) {
    return null;
  }
  if (children && children.props && children.props.tx) {
    // console.log(children.props.tx);
    logger.info(`Showing infobox with message: ${translate(children.props.tx)}`);
  }
  const { borderColor, backgroundColor } = getBorderAndFillColor(infoBoxType, theme);
  const styles = StyleSheet.create({
    container: {
      display: "flex",
      width,
      flexDirection: "column",
      padding: theme.spacing.small,
      borderWidth: 1,
      borderColor,
      backgroundColor,
      borderRadius: borderRadius,
    },
    headlineContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    iconWidth: {
      width: theme.iconSize.smaller,
      height: theme.iconSize.smaller,
    },
    headlineStyle: {
      fontWeight: "bold",
      marginLeft: theme.spacing.smaller,
      width: headlineWidth,
      fontSize: theme.fontSize.medium,
    },
  });
  const xCloseHandler = () => {
    setIsClosed(true);
  };
  const headlineIcon = getHeadlineIcon(headlineIconType, "attention", theme);
  return (
    <Stack space="smaller" useThemeBackgroundColor={false} style={styles.container}>
      <View style={styles.headlineContainer}>
        {headlineIcon}
        <Text style={styles.headlineStyle}>{headline}</Text>
        {usesXClose ? (
          <TouchableRipple onPress={xCloseHandler}>
            <Image
              onPress={setIsClosed}
              style={styles.iconWidth}
              source={require("../../../assets/x.png")}></Image>
          </TouchableRipple>
        ) : null}
      </View>
      {children}
    </Stack>
  );
};

const getBorderAndFillColor = (type: InfoBoxType, theme: ThemeType): ViewStyle => {
  switch (type) {
    case InfoBoxType.GREEN:
      return {
        borderColor: theme.colors.palette.lightGreen,
        backgroundColor: theme.colors.lightSuccess,
      };

    case InfoBoxType.ORANGE:
      return {
        borderColor: theme.colors.palette.lightOrange,
        backgroundColor: theme.colors.lightWarning,
      };

    case InfoBoxType.BLUE:
      return {
        borderColor: theme.colors.palette.lightBlue,
        backgroundColor: theme.colors.lightInfo,
      };
    case InfoBoxType.RED:
      return {
        borderColor: theme.colors.palette.lightRed,
        backgroundColor: theme.colors.lightError,
      };

    default:
      return {
        borderColor: theme.colors.palette.lightGreen,
        backgroundColor: theme.colors.lightSuccess,
      };
  }
};

const getHeadlineIcon = (headlineIconType: InfoBoxIconType, iconName: string, theme: ThemeType) => {
  const iconSize = {
    height: theme.iconSize.small,
    width: theme.iconSize.small,
    resizeMode: "contain",
  };
  switch (headlineIconType) {
    case InfoBoxIconType.EXCLAMATION:
      return (
        <AntDesign
          name="exclamationcircleo"
          size={theme.iconSize.small}
          color="black"
          accessible={true}
          accessibilityLabel={iconName}
        />
      );

    case InfoBoxIconType.INFORMATION:
      return (
        <AntDesign
          name="infocirlceo"
          size={theme.iconSize.small}
          color="black"
          accessible={true}
          accessibilityLabel={iconName}
        />
      );

    case InfoBoxIconType.GREEN_CHECK:
      return (
        <Image
          style={iconSize}
          source={require("../../../assets/green_check.png")}
          accessible={true}
          accessibilityLabel={iconName}
        />
      );

    case InfoBoxIconType.RED_X:
      return (
        <Image
          style={iconSize}
          source={require("../../../assets/red_x.png")}
          accessible={true}
          accessibilityLabel={iconName}
        />
      );

    default:
      return (
        <AntDesign
          name="exclamationcircleo"
          size={theme.iconSize.small}
          color="black"
          accessible={true}
          accessibilityLabel={iconName}
        />
      );
  }
};
